






import Vue from 'vue';
import { mapActions, mapMutations } from 'vuex';
import { HotelDataInterface } from '@/types/store/landing/hotels/hotelDataInterface';
import { RoomDataInterface } from '@/types/store/landing/hotels/roomDataInterface';
import { BookingDataInterface } from '@/types/store/landing/booking/bookingDataInterface';
import { PaymentSystem } from '@/constants/paymentSystem';
import { PaymentMethods } from '@/constants/paymentMethods';
import { Source } from '@/constants/source';
import { GoogleAdsTrackingInterface } from '@/types/settingsInterface';

interface AppContextInterface {
  locale: string;
  current_hotel: HotelDataInterface;
  room_type_data: RoomDataInterface;
  booking_data: BookingDataInterface;
  payment_methods: PaymentMethods[];
  payment_system: PaymentSystem;
  payment_system_activated: boolean;
  account_source: Source;
  google_ads_tracking: GoogleAdsTrackingInterface;
  recaptcha_site_key: string;
  testing_mode: boolean;
  stripe_public_key: string;
  gmo_shop_id: string;
  go_to_campaign: boolean;
}

export default Vue.extend({
  name: 'HotelLandingApp',
  props: {
    appContext: {
      type: Object as () => AppContextInterface,
    },
  },
  watch: {
    '$i18n.locale'() {
      if (this.appContext.current_hotel) {
        document.title = this.appContext.current_hotel.name[this.i18nTag()]
          ? this.appContext.current_hotel.name[this.i18nTag()]
          : this.appContext.current_hotel.name.en;
      }
    },
  },
  created(): void {
    if (this.appContext.locale) {
      this.$i18n.locale = this.appContext.locale;
      localStorage.locale = this.appContext.locale;
    } else {
      const locale: string = this.getDefaultLocaleByBrowser();
      this.$i18n.locale = locale;
      localStorage.locale = locale;
    }
    this.changeMaterialLocation();
    const settings: any = {};
    const keys = [
      'account_source',
      'gmo_shop_id',
      'payment_system',
      'payment_system_activated',
      'stripe_public_key',
      'google_ads_tracking',
      'booking_data',
      'recaptcha_site_key',
      'testing_mode',
    ];
    keys.forEach((key: string) => {
      if (this.appContext.hasOwnProperty(key)) {
        settings[key] = this.appContext[key as keyof AppContextInterface];
      }
    });
    if (Object.keys(settings).length > 0) {
      this.setSettings(settings);
    }
    if (this.appContext.current_hotel) {
      const hotel: HotelDataInterface = this.appContext.current_hotel;
      hotel.in_campaign = this.appContext.go_to_campaign;
      document.title = hotel.name[this.i18nTag()] ? hotel.name[this.i18nTag()] : hotel.name.en;
      hotel.cancellation_policy.days = hotel.cancellation_policy.days_before_start;
      hotel.cancellation_policy.default_rate = Math.round(100 * hotel.cancellation_policy.default_rate);
      hotel.cancellation_policy.higher_rate = Math.round(100 * hotel.cancellation_policy.higher_rate);
      if (!this.$store.state.landing.hotel.item || this.$store.state.landing.hotel.item.hotel_hid !== hotel.hotel_hid) {
        this.appContext.current_hotel = hotel;
        this.$store.dispatch('landing/clearAll', this.appContext);
      } else {
        this.updateHotel(hotel);
      }
    }
    if (this.appContext.room_type_data) {
      const url = window.location.href;
      if (url.indexOf('payment') === -1 && url.indexOf('guest-info') === -1) {
        this.setRoomInfo(this.appContext.room_type_data);
      }
    }
    if (this.appContext.booking_data) {
      const bookingData = { ...JSON.parse(JSON.stringify(this.appContext.booking_data)) };
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const checkinContext = new Date(this.appContext.booking_data.checkin);
      const checkoutContext = new Date(this.appContext.booking_data.checkout);
      if (checkinContext < today || checkoutContext < today) {
        bookingData.checkin = Vue.filter('date')(today);
        bookingData.checkout = Vue.filter('date')(Vue.filter('nextDay')(today));
      }
      this.setInitBooking(bookingData);
    }
  },
  mounted() {
    this.checkDateValidation();
  },
  methods: {
    ...mapMutations({
      setRoomInfo: 'landing/hotel/setRoomInfo',
      setInitBooking: 'landing/booking/setInitBooking',
      updateHotel: 'landing/hotel/setHotel',
      setSettings: 'landing/setSettings',
    }),
    ...mapActions({
      changeMaterialLocation: 'calendar/changeMaterialLocation',
    }),
    checkDateValidation(): void {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const checkinContext = new Date(this.appContext.booking_data.checkin);
      const checkoutContext = new Date(this.appContext.booking_data.checkout);
      if (checkinContext < today || checkoutContext < today) {
        Vue.notify({
          group: 'notify',
          text: this.$i18n.t('period-unavailable') as string,
          type: 'error',
        });
      }
    },
  },
});
